import { useCallback, useEffect, useRef } from "react";
import { useUnit } from "effector-react";
import { button, useControls } from "leva";
import { Vector3 } from "three";
import type { Schema } from "leva/dist/declarations/src/types/public.js";
import { $currentSight, $sceneBindings, updateSightCameraConfig } from "../../state/appState";
import { computeSphericalFromCamera } from "./computeSphericalFromCamera";

type ChangeContext = { initial?: boolean };

const EDIT_COOLDOWN_MS = 500;

/** Module-level scratch — this panel writes it from a rAF-adjacent effect. */
const _pinnedTarget = new Vector3();

const mkNumber = (
  key: string,
  value: number,
  min: number,
  max: number,
  step: number,
  label: string,
  onChange: (key: string, value: number) => void,
  isProgrammaticRef: React.RefObject<boolean>,
) => ({
  value,
  min,
  max,
  step,
  label,
  onChange: (v: number, _path: string, ctx: ChangeContext) => {
    if (ctx.initial || isProgrammaticRef.current) return;
    onChange(key, v);
  },
});

export const SightCameraConfigPanel = () => {
  const { currentSight, sceneBindings } = useUnit({
    currentSight: $currentSight,
    sceneBindings: $sceneBindings,
  });
  const pushSightCameraConfig = useUnit(updateSightCameraConfig);

  const isEditingRef = useRef(false);
  const isProgrammaticSetRef = useRef(false);
  const editTimeoutRef = useRef(0);
  const rafRef = useRef(0);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getLevaValueRef = useRef<(path: string) => any>(() => 0);

  const currentSightRef = useRef(currentSight);
  currentSightRef.current = currentSight;

  const sceneBindingsRef = useRef(sceneBindings);
  sceneBindingsRef.current = sceneBindings;

  const handleConfigChange = useCallback(
    (key: string, value: number) => {
      const sight = currentSightRef.current;
      const bindings = sceneBindingsRef.current;
      if (!sight || !bindings) return;

      isEditingRef.current = true;
      window.clearTimeout(editTimeoutRef.current);
      editTimeoutRef.current = window.setTimeout(() => {
        isEditingRef.current = false;
      }, EDIT_COOLDOWN_MS);

      pushSightCameraConfig({
        sightId: sight.id,
        cameraConfig: { [key]: value },
      });

      const updatedSight = {
        ...sight,
        cameraConfig: { ...sight.cameraConfig, [key]: value },
      };
      bindings.sightManager.moveToSight(updatedSight);
    },
    [pushSightCameraConfig],
  );

  const config = currentSight?.cameraConfig;

  const buildSchema = useCallback((): Schema => {
    if (!currentSight) return {};

    const mk = (key: string, value: number, min: number, max: number, step: number, label: string) =>
      mkNumber(key, value, min, max, step, label, handleConfigChange, isProgrammaticSetRef);

    return {
      // Ranges sized for the largest current scene (Arafat: sights up to ~25m from
      // origin, elevated jabal-rahma at y=5). Leva silently clamps set() values that
      // exceed the slider max, so tight ranges corrupt captured configs.
      distance: mk("distance", config?.distance ?? 0.5, 0.05, 100, 0.01, "Distance"),
      height: mk("height", config?.height ?? 0, -20, 20, 0.01, "Height"),
      azimuth: mk("azimuth", config?.azimuth ?? 0, -Math.PI * 2, Math.PI * 2, 0.01, "Azimuth (rad)"),
      polar: mk("polar", config?.polar ?? Math.PI / 2, 0.01, Math.PI - 0.01, 0.01, "Polar (rad)"),
      orbitAzimuthMin: mk("orbitAzimuthMin", config?.orbitAzimuthMin ?? -Math.PI / 4, -Math.PI, 0, 0.01, "Orbit Az Min"),
      orbitAzimuthMax: mk("orbitAzimuthMax", config?.orbitAzimuthMax ?? Math.PI / 4, 0, Math.PI, 0.01, "Orbit Az Max"),
      orbitPolarMin: mk("orbitPolarMin", config?.orbitPolarMin ?? -Math.PI / 6, -Math.PI / 2, 0, 0.01, "Orbit Pol Min"),
      orbitPolarMax: mk("orbitPolarMax", config?.orbitPolarMax ?? Math.PI / 6, 0, Math.PI / 2, 0.01, "Orbit Pol Max"),
      "Copy JSON": button(() => {
        const g = getLevaValueRef.current;
        const output = {
          distance: g("distance"),
          height: g("height"),
          azimuth: g("azimuth"),
          polar: g("polar"),
          orbitAzimuthMin: g("orbitAzimuthMin"),
          orbitAzimuthMax: g("orbitAzimuthMax"),
          orbitPolarMin: g("orbitPolarMin"),
          orbitPolarMax: g("orbitPolarMax"),
        };
        const json = JSON.stringify(output, null, 4);
        navigator.clipboard?.writeText(json);
        const sight = $currentSight.getState();
        // eslint-disable-next-line no-console
        console.log(`[SightCameraConfig] ${sight?.name ?? sight?.id ?? "?"}:\n${json}`);
      }),
    };
  }, [currentSight, config, handleConfigChange]);

  // Separate `useControls` call with a STATIC schema on purpose: the main one
  // re-initialises whenever `buildSchema` changes (every slider edit re-seeds it
  // from `sight.cameraConfig`), which would flip this toggle back off mid-tuning.
  const [{ pinOrbitTarget }] = useControls(
    "Sight Camera Config",
    () => ({
      pinOrbitTarget: { value: false, label: "Pin target to sight" },
    }),
    [],
  );

  const [, set, get] = useControls(
    "Sight Camera Config",
    buildSchema,
    [buildSchema],
  );
  getLevaValueRef.current = get;

  // Opt-in: pin the orbit target to the sight being tuned.
  //
  // The panel reports the camera in spherical coords AROUND THE SIGHT, but the
  // scene's orbit clamps (`orbitMaxPolarAngle`, height clamps) and its
  // colliders are evaluated around `controls.target`. Grabbing the scene
  // settles that target back to the scene centre, so from that moment the panel
  // is measuring a pose composed under *different* constraints than
  // `transitionToSight` will later apply — the copied JSON can then resolve to
  // a pose the runtime clamps and fights. Pinning makes what you compose here
  // reachable by construction.
  //
  // OFF by default: the settle-back-to-centre behaviour is product behaviour,
  // and suppressing it for the whole time the panel is open changed how the
  // scene felt under `?dev`. Turn it on only while authoring a POI pose.
  useEffect(() => {
    const sight = currentSightRef.current;
    if (!sceneBindings || !sight || !pinOrbitTarget) return;

    const { controls, sightManager, requestRender } = sceneBindings;
    controls.setOrbitReturnOnInteraction(false);
    // Mid-flight the transition owns the target and sets it every frame; only
    // seed it when nothing else is writing.
    if (!sightManager.isInTransition()) {
      _pinnedTarget.set(sight.position.x, sight.position.y, sight.position.z);
      controls.setOrbitTargetOverride(_pinnedTarget);
      requestRender();
    }

    return () => {
      controls.setOrbitReturnOnInteraction(true);
    };
  }, [sceneBindings, currentSight?.id, pinOrbitTarget]);

  // rAF loop: read camera position → update leva sliders (when not editing)
  useEffect(() => {
    if (!sceneBindings || !currentSight) return;

    const tick = () => {
      const sight = currentSightRef.current;
      const bindings = sceneBindingsRef.current;

      if (sight && bindings && !isEditingRef.current) {
        const spherical = computeSphericalFromCamera(
          bindings.camera.position,
          sight.position,
          sight.cameraConfig?.height ?? 0,
        );

        // Guard: set() triggers onChange synchronously via zustand subscription.
        // isProgrammaticSetRef prevents the onChange handler from firing back.
        isProgrammaticSetRef.current = true;
        set({
          distance: spherical.distance,
          height: spherical.height,
          azimuth: spherical.azimuth,
          polar: spherical.polar,
        });
        isProgrammaticSetRef.current = false;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [sceneBindings, currentSight?.id, set]);

  return null;
};
